import { PageHeader } from "@/components/PageHeader";
import { ProductCard } from "@/components/ProductCard";
import { ComparisonTable } from "@/components/ComparisonTable";
import { PricingSection } from "@/components/sections/PricingSection";
import { FaqSection } from "@/components/sections/FaqSection";
import { CtaBanner } from "@/components/sections/CtaBanner";
import { TrustMarquee } from "@/components/sections/TrustMarquee";
import { useProducts } from "@/hooks/useProducts";
import { Truck, ShieldCheck, RotateCcw, Phone } from "lucide-react";
import { BRAND } from "@/data/content";

const perks = [
  { icon: Truck, label: "Free US shipping" },
  { icon: ShieldCheck, label: "UL-Listed devices" },
  { icon: RotateCcw, label: "Cancel any time" },
];

export default function Shop() {
  const { data: products = [], isLoading } = useProducts();

  return (
    <div data-testid="shop-page">
      <PageHeader
        eyebrow="Our Devices"
        title={<>Medical alerts built for <span className="text-brand-red">real life.</span></>}
        sub="Every device connects to the same 24/7 US-based monitoring center. One-time purchase, free shipping, and no long-term contract."
      >
        <div className="flex flex-wrap gap-3">
          {perks.map((p) => (
            <span key={p.label} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white font-semibold">
              <p.icon className="w-5 h-5 text-brand-red" />
              {p.label}
            </span>
          ))}
        </div>
      </PageHeader>

      <TrustMarquee />

      <section className="py-16 lg:py-24 bg-brand-bg">
        <div className="container-x">
          {isLoading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-[560px] rounded-3xl bg-white border border-brand-line animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {products.map((p, i) => (
                <ProductCard key={p.lookup_key} product={p} index={i} />
              ))}
            </div>
          )}

          <div className="mt-16" data-testid="shop-comparison">
            <span className="eyebrow text-brand-red">
              <span className="inline-block h-px w-7 bg-brand-red" />
              Side by Side
            </span>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-brand-navy">
              Compare all four devices.
            </h2>
            <p className="mt-2 text-brand-ink2">Battery, fall detection, coverage, and price — so you can choose faster.</p>
            <div className="mt-6">
              <ComparisonTable />
            </div>
          </div>

          <div className="mt-12 rounded-3xl bg-brand-navy text-white p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display font-bold text-2xl">Not sure which device fits?</h3>
              <p className="mt-2 text-white/70">Talk to a US-based specialist — free, no pressure, no obligation.</p>
            </div>
            <a
              href={BRAND.phoneHref}
              data-testid="shop-call-specialist"
              className="btn-focus inline-flex items-center gap-2.5 h-14 px-7 rounded-full bg-brand-red text-white font-bold text-lg hover:bg-brand-red-600 transition-colors shrink-0"
            >
              <Phone className="w-5 h-5" strokeWidth={2.6} />
              {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      <PricingSection compact />
      <FaqSection limit={5} />
      <CtaBanner />
    </div>
  );
}
